import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { AddressStep } from "@/components/setup/AddressStep";
import { BankStep } from "@/components/setup/BankStep";
import { ProviderStep } from "@/components/setup/ProviderStep";
import { StepIndicator } from "@/components/setup/StepIndicator";
import { Button } from "@/components/ui/Button";
import { SetupWizardContext, useSetupWizard } from "@/hooks/useSetupWizard";
import * as shared from "@/styles/shared";
import { Step } from "@shared/types/setup";
import { CheckCircle2, ChevronLeft, ClipboardList, SkipForward } from "lucide-react";
import { useEffect } from "react";
import * as styles from "./SetupWizardPage.styles";

const SetupWizardPage = () => {
  const { contextValue } = useSetupWizard();

  useEffect(() => {
    void contextValue.checkSetupStatus();
  }, [contextValue.checkSetupStatus]);

  if (contextValue.success) {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.successContainer}>
            <div className={styles.successIcon}>
              <CheckCircle2 className={styles.successIconInner} />
            </div>
            <h2 className={styles.successTitle}>{contextValue.t("setupCompleteMsg")}</h2>
          </div>
        </div>
      </div>
    );
  }

  return (
    <SetupWizardContext value={contextValue}>
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <div className={styles.cardHeaderRow}>
              <div className={styles.cardHeaderTitleGroup}>
                <div className={styles.titleRow}>
                  <ClipboardList className={styles.titleIcon} />
                  <h1 className={styles.cardTitle}>{contextValue.t("pageTitle")}</h1>
                </div>
                <p className={shared.muted}>{contextValue.t("welcomeDesc")}</p>
              </div>
              <LanguageSwitcher />
            </div>
          </div>

          <StepIndicator currentStep={contextValue.currentStep} t={contextValue.t} stepOrder={contextValue.stepOrder} />

          <div className={styles.content}>
            {contextValue.error && <div className={styles.error}>{contextValue.error}</div>}

            {contextValue.currentStep === Step.Bank && <BankStep />}
            {contextValue.currentStep === Step.Address && <AddressStep />}
            {contextValue.currentStep === Step.Provider && <ProviderStep />}

            <div className={styles.actions}>
              {contextValue.currentStep !== Step.Bank ? (
                <button
                  onClick={() => contextValue.setCurrentStep(contextValue.currentStep === Step.Provider ? Step.Address : Step.Bank)}
                  className={styles.backBtn}
                >
                  <ChevronLeft className={styles.backBtnIcon} />
                  {contextValue.t("back")}
                </button>
              ) : (
                <span />
              )}
              <div className={shared.rowGap3}>
                <button onClick={contextValue.handleSkip} className={styles.skipBtn} disabled={contextValue.loading}>
                  <SkipForward className={styles.skipBtnIcon} />
                  {contextValue.t("skip")}
                </button>
                {contextValue.currentStep === Step.Bank && (
                  <Button
                    onClick={contextValue.handleBankSubmit}
                    disabled={contextValue.loading || !contextValue.bankName.trim()}
                    className={styles.primaryBtn}
                  >
                    {contextValue.loading ? contextValue.t("creatingBank") : contextValue.t("next")}
                  </Button>
                )}
                {contextValue.currentStep === Step.Address && (
                  <Button
                    onClick={contextValue.handleAddressSubmit}
                    disabled={contextValue.loading || !contextValue.addressName.trim()}
                    className={styles.primaryBtn}
                  >
                    {contextValue.loading ? contextValue.t("creatingAddress") : contextValue.t("next")}
                  </Button>
                )}
                {contextValue.currentStep === Step.Provider && (
                  <Button
                    onClick={contextValue.handleProviderSubmit}
                    disabled={contextValue.loading || !contextValue.selectedProviderId}
                    className={styles.primaryBtn}
                  >
                    {contextValue.loading ? (
                      contextValue.t("completingSetup")
                    ) : (
                      <>
                        {contextValue.t("finish")}
                        <CheckCircle2 className={styles.finishIcon} />
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </SetupWizardContext>
  );
};

export { SetupWizardPage };
